import {
  type IFeaturesResponse,
  type Features,
  FeatureNames,
  type ResponseFeature,
  WebsiteABtestVariant,
  InstallCodeStepABtestVariant,
} from './interfaces';

const knownFeatures = new Map<string, string>([
  ['testFeature', 'test_feature'],
  [FeatureNames.ChatsListPagination, 'chats_list_pagination'],
  [FeatureNames.RichTextEditor, 'rich_text_editor'],
  [FeatureNames.DisturbingContentDetection, 'disturbing_content_detection'],
  [FeatureNames.CustomerIntentEnabled, 'customer_intent_enabled'],
  [FeatureNames.ReviewsReportEnabled, 'reviews_report_enabled'],
  [FeatureNames.InsightsReportsEnabled, 'recent_insights_enabled'],
  [FeatureNames.AISuggestions, 'ai_suggestions'],
  [FeatureNames.ProductSwitcher, 'product_switcher'],
  [FeatureNames.PriorityChat, 'priority_chat'],
  [FeatureNames.KnowledgeHubAddWebsiteOnboarding, 'knowledge_hub_add_website_onboarding'],
  [FeatureNames.AICannedInChips, 'ai_canned_in_chips'],
  [FeatureNames.ReplySuggestionsFullMessageContext, 'rs-full-messages-context'],
  [FeatureNames.KnowledgeHubPdfCustomLimit, 'kh_custom_pdf_limit'],
  [FeatureNames.OnboardingProvideWebsiteABtest, 'onboarding-ab-test-website-copy'],
  [FeatureNames.OnboardingInstallCodeStepABtest, 'onboarding-ab-test-install-code-step'],
  [FeatureNames.GreetingsConfApiEnabled, 'greetings_confapi_enabled'],
  [FeatureNames.TextAppRedirectsEnabled, 'text_app_redirects_enabled'],
  [FeatureNames.MigrateCampaignConversionTo37, 'migrate-campaing-conversion-to-3.7'],
  [FeatureNames.LegacyProductsMigrationCampaign, 'legacy_products_migration_campaign'],
  [FeatureNames.TextAppWhitelisted, 'text_app_whitelisted'],
]);

export const defaultFeatures: Features = {
  [FeatureNames.TestFeature]: false,
  [FeatureNames.ChatsListPagination]: false,
  [FeatureNames.RichTextEditor]: false,
  [FeatureNames.DisturbingContentDetection]: false,
  [FeatureNames.CustomerIntentEnabled]: false,
  [FeatureNames.ReviewsReportEnabled]: true,
  [FeatureNames.InsightsReportsEnabled]: false,
  [FeatureNames.AISuggestions]: false,
  [FeatureNames.ProductSwitcher]: false,
  [FeatureNames.PriorityChat]: false,
  [FeatureNames.KnowledgeHubAddWebsiteOnboarding]: false,
  [FeatureNames.AICannedInChips]: false,
  [FeatureNames.ReplySuggestionsFullMessageContext]: false,
  [FeatureNames.KnowledgeHubPdfCustomLimit]: {
    enabled: false,
    limit: 0,
  },
  [FeatureNames.OnboardingProvideWebsiteABtest]: {
    value: true,
    variant: {
      name: WebsiteABtestVariant.VariantA,
    },
  },
  [FeatureNames.OnboardingInstallCodeStepABtest]: {
    value: true,
    variant: {
      name: InstallCodeStepABtestVariant.Visible,
    },
  },
  [FeatureNames.GreetingsConfApiEnabled]: false,
  [FeatureNames.TextAppRedirectsEnabled]: true,
  [FeatureNames.MigrateCampaignConversionTo37]: false,
  [FeatureNames.LegacyProductsMigrationCampaign]: false,
  [FeatureNames.TextAppWhitelisted]: false,
};

const serializeABTestFeature = (feature: ResponseFeature) => ({
  value: feature.value,
  variant: {
    name: feature.variant?.name,
  },
});

const CustomFeaturesSerializer = {
  [FeatureNames.KnowledgeHubPdfCustomLimit]: (feature: ResponseFeature) => {
    return {
      enabled: feature.value,
      limit: feature.variant?.payload?.value ? parseInt(feature.variant.payload.value, 10) : 0,
    };
  },
  [FeatureNames.OnboardingProvideWebsiteABtest]: serializeABTestFeature,
  [FeatureNames.OnboardingInstallCodeStepABtest]: serializeABTestFeature,
};

export function deserializeFeatures(features: IFeaturesResponse): Features {
  return Object.entries(defaultFeatures).reduce((result, [key, value]) => {
    const feature = features.feature_flags.find(({ name }) => name === knownFeatures.get(key));

    if (feature && key in CustomFeaturesSerializer) {
      result[key] = CustomFeaturesSerializer[key as keyof typeof CustomFeaturesSerializer](feature);
    } else {
      result[key] = feature?.value ?? value;
    }

    return result;
  }, {} as Features);
}
